import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useProfileStatus } from '../hooks/useProfessionalProfile';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { Card, Button } from '../components';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  Users,
  CheckCircle,
  Clock,
  ArrowRight,
  Search,
  Building,
  Link2
} from 'lucide-react';

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const profileStatus = useProfileStatus();
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats();

  // Redirect to role-specific dashboards
  useEffect(() => {
    if (user?.role === 'opdrachtgever' || user?.role === 'employer_member') {
      navigate('/employer/dashboard', { replace: true });
    } else if (user?.role === 'professional') {
      navigate('/professional/dashboard', { replace: true });
    } else if (user?.role === 'medewerker') {
      navigate('/medewerker', { replace: true });
    } else if (user?.role === 'opleider') {
      navigate('/opleider/dashboard', { replace: true });
    }
  }, [user?.role, navigate]);

  // Show loading during redirect
  if (
    user?.role === 'opdrachtgever' ||
    user?.role === 'employer_member' ||
    user?.role === 'professional' ||
    user?.role === 'medewerker' ||
    user?.role === 'opleider'
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('dashboardPage.redirecting')}</p>
        </div>
      </div>
    );
  }

  // Show loading state for professionals while checking onboarding status
  if ((user?.role as string) === 'professional' && profileStatus.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('dashboardPage.loadingProfile')}</p>
        </div>
      </div>
    );
  }

  const getOnboardingStep = () => {
    if (user?.role !== 'professional' || !profileStatus.data) return null;
    const data = profileStatus.data as any;
    const profile = data.profile || data.data?.profile;
    return profile?.onboarding_step || null;
  };

  const onboardingStep = getOnboardingStep();

  const stats = (user?.role as string) === 'opdrachtgever' ? [
    {
      name: t('dashboardPage.availableProfessionals'),
      value: statsLoading ? '...' : String(dashboardStats?.total_professionals || 0),
      icon: Users,
      color: 'bg-blue-500',
      href: '/employer/dashboard/zoeken/uitgebreid',
    },
    {
      name: t('dashboardPage.myConnections'),
      value: '0',
      icon: Link2,
      color: 'bg-purple-500',
      href: '/employer/connections',
    },
    {
      name: t('dashboardPage.activeVacancies'),
      value: '0',
      icon: Briefcase,
      color: 'bg-green-500',
      href: '/employer/dashboard/zoeken?route=vacatures',
    },
    {
      name: t('dashboardPage.messages'),
      value: statsLoading ? '...' : String(dashboardStats?.unread_messages || 0),
      icon: CheckCircle,
      color: 'bg-yellow-500',
      href: '/berichten',
    },
  ] : [
    {
      name: t('dashboardPage.activeConnections'),
      value: statsLoading ? '...' : String(dashboardStats?.active_connections || 0),
      icon: Link2,
      color: 'bg-blue-500',
      href: '/employer/connections',
    },
    {
      name: t('dashboardPage.assignments'),
      value: statsLoading ? '...' : String(dashboardStats?.search_requests || 0),
      icon: Search,
      color: 'bg-green-500',
      href: '/opdrachten',
    },
    {
      name: t('dashboardPage.messages'),
      value: statsLoading ? '...' : String(dashboardStats?.unread_messages || 0),
      icon: Users,
      color: 'bg-purple-500',
      href: '/berichten',
    },
  ];

  const quickActions = (user?.role as string) === 'opdrachtgever'
    ? [
        {
          name: t('dashboardPage.searchProfessionals'),
          href: '/employer/dashboard/zoeken/uitgebreid',
          icon: Search,
          description: t('dashboardPage.searchProfessionalsDesc'),
        },
        {
          name: t('dashboardPage.myConnections'),
          href: '/employer/connections',
          icon: Link2,
          description: t('dashboardPage.myConnectionsDesc'),
        },
        {
          name: t('dashboardPage.companyProfile'),
          href: '/employer/profile',
          icon: Building,
          description: t('dashboardPage.companyProfileDesc'),
        },
        {
          name: t('dashboardPage.messages'),
          href: '/berichten',
          icon: Users,
          description: t('dashboardPage.messagesDesc'),
        },
      ]
    : [
        {
          name: t('dashboardPage.searchAssignments'),
          href: '/opdrachten',
          icon: Search,
          description: t('dashboardPage.searchAssignmentsDesc'),
        },
        {
          name: t('dashboardPage.messages'),
          href: '/berichten',
          icon: Users,
          description: t('dashboardPage.messagesReadDesc'),
        },
      ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboardPage.welcomeBack', { name: user?.first_name })}
        </h1>
        <p className="mt-1 text-gray-600">
          {(user?.role as string) === 'opdrachtgever'
            ? t('dashboardPage.employerSubtitle')
            : t('dashboardPage.professionalSubtitle')}
        </p>
      </div>

      {/* Professional Screening Banners */}
      {(user?.role as string) === 'professional' && onboardingStep && onboardingStep !== 'profile_complete' && (
        <div className="mb-8">
          {onboardingStep === 'registration' && (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboardPage.screeningBannerTitle')}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {t('dashboardPage.screeningBannerText')}
                  </p>
                  <Button onClick={() => navigate('/professional/screening')} size="lg">
                    {t('dashboardPage.startScreening')}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {onboardingStep === 'screening_pending' && (
            <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center animate-pulse">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboardPage.screeningPendingTitle')}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {t('dashboardPage.screeningPendingText')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => navigate('/professional/contract')}>
                      {t('dashboardPage.signAgreementNow')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/professional/profile')}
                    >
                      {t('dashboardPage.setupProfileNow')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/professional/screening/pending')}
                    >
                      {t('dashboardPage.viewStatus')}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {(onboardingStep === 'screening_approved' || onboardingStep === 'contract_pending') && (
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboardPage.screeningApprovedTitle')}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {t('dashboardPage.screeningApprovedText')}
                  </p>
                  <Button onClick={() => navigate('/professional/contract')} size="lg">
                    {t('dashboardPage.signContract')}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {onboardingStep === 'contract_signed' && (
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('dashboardPage.contractSignedTitle')}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {t('dashboardPage.contractSignedText')}
                  </p>
                  <Button onClick={() => navigate('/professional/profile')} size="lg">
                    {t('dashboardPage.completeProfile')}
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.name} to={stat.href} className="transition-transform hover:scale-105">
              <Card padding={false} className="cursor-pointer hover:shadow-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 ${stat.color} rounded-md flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {stat.name}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {stat.value}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <Card>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('dashboardPage.quickActions')}
            </h3>
            <div className="space-y-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={action.name}
                    to={action.href}
                    className="flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <Icon className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        {action.name}
                      </span>
                      {action.description && (
                        <span className="text-xs text-gray-500">
                          {action.description}
                        </span>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Profile Completion */}
      {(user?.role as string) === 'professional' && profileStatus?.data && !profileStatus.data.data.profile_complete && (
        <div className="mt-8">
          <Card className="bg-yellow-50 border-yellow-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <Clock className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t('dashboardPage.profileIncomplete')}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{t('dashboardPage.profileIncompleteText')}</p>
                </div>
                <div className="mt-4">
                  <Link to="/professional/profile">
                    <Button variant="outline" size="sm" className="border-yellow-300 text-yellow-800 hover:bg-yellow-100">
                      {t('dashboardPage.completeProfileBtn')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
